import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ORDER_METHODS } from '../../services/exports/Constants';
import { OnSiteTableSelection } from '../../services/exports/Interfaces';
import { StoreInterface } from '../../store/types';
import { actionCreators } from '../../store/actions';
import { setTableNotice } from '../../store/slices/OrderMethodSlice';
import { formatTableName, isOrderAtTable, isTableSelectionActive } from '../../utils/on-site';

export interface TableOrderInterface {
  /** The table chosen from a QR code at this restaurant, this visit; also while the guest is in pickup. */
  table: OnSiteTableSelection | null;
  /** Ordering to that table right now. */
  isAtTable: boolean;
  /**
   * The cart is a table order, whether or not its table still applies: SideEffectsProvider drops a table
   * whose visit ran out (the backend has no expiry on a table), but only once no payment is in flight.
   */
  isTableOrder: boolean;
  /** "Table 7", or "Terrace 2" as it is. */
  tableLabel: string;
  /** Leaves the table for `method` (pickup unless the restaurant doesn't offer it). */
  leaveTable: (method?: ORDER_METHODS) => void;
  returnToTable: () => void;
}

export default function useTableOrder(): TableOrderInterface {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:TableOrdering' });
  const cachedOrder = useSelector((state: StoreInterface) => state.order.cached_order);
  const companyId = useSelector((state: StoreInterface) => state.initialData.company?.id);
  const dispatch = useDispatch();

  const table = isTableSelectionActive(cachedOrder?.on_site_table, companyId) ? cachedOrder.on_site_table : null;
  const isAtTable = isOrderAtTable(cachedOrder, companyId);

  // The table stays on the cart, so "Eating here? Order to Table 7" can switch back to it. The guest
  // chose, so a note about the table (a re-scan that failed) no longer applies.
  const leaveTable = useCallback(
    (method: ORDER_METHODS = ORDER_METHODS.PICKUP) => {
      dispatch(actionCreators.order.updateOrder({ method }));
      dispatch(setTableNotice(null));
    },
    [dispatch],
  );

  const returnToTable = useCallback(() => {
    dispatch(actionCreators.order.updateOrder({ method: ORDER_METHODS.ON_SITE, scheduled_for: null }));
    dispatch(setTableNotice(null));
  }, [dispatch]);

  return {
    table,
    isAtTable,
    isTableOrder: cachedOrder?.method === ORDER_METHODS.ON_SITE,
    tableLabel: formatTableName(table?.name, t('table')),
    leaveTable,
    returnToTable,
  };
}
